import type {
  ChangelogSettings,
  ChangelogSettingsInput,
} from "@/features/changelog/api/changelog.api";

/** Fields the page renders. The credit and the Slack flag are read-only. */
export const CHANGELOG_SETTING_KEYS = [
  "requireDeployApproval",
  "requireRoundupApproval",
  "autoPublishDelayMinutes",
  "quietHoursEnabled",
  "quietHoursStartMinute",
  "quietHoursEndMinute",
  "timezone",
  "minItemsToAutoPublish",
  "userFacingAnnouncements",
  "autoComposeRoundups",
  "minDeploysToAutoCompose",
  "roundupAutoSendDelayMinutes",
  "roundupNotifyEmail",
  "roundupNotifySlack",
  "sprintLengthDays",
  "sprintAnchorDate",
  "sprintAnchorNumber",
  "roundupCadence",
  "notesAudience",
  "includeJiraLinks",
  "maxItemsPerSection",
  "autoSyncOnOpen",
  "notifyOnFailure",
] as const satisfies ReadonlyArray<keyof ChangelogSettingsInput>;

export type ChangelogSettingKey = (typeof CHANGELOG_SETTING_KEYS)[number];

/** The editable half of a settings record, which is what the form holds. */
export type ChangelogSettingsDraft = Pick<ChangelogSettings, ChangelogSettingKey>;

export function toDraft(settings: ChangelogSettings): ChangelogSettingsDraft {
  return CHANGELOG_SETTING_KEYS.reduce((draft, key) => {
    // The index signature is lost by `reduce`, and the alternative is writing
    // all seventeen assignments out by hand and keeping them in step forever.
    (draft as Record<string, unknown>)[key] = settings[key];
    return draft;
  }, {} as ChangelogSettingsDraft);
}

/**
 * The fields that differ from what the server last returned.
 *
 * Saving the difference rather than the whole record means two admins editing
 * separate sections do not overwrite each other, and a field this build does
 * not know about is never sent back as a value it guessed.
 */
export function changedSettings(
  draft: ChangelogSettingsDraft,
  saved: ChangelogSettings,
): Partial<ChangelogSettingsInput> {
  const changes: Record<string, unknown> = {};
  for (const key of CHANGELOG_SETTING_KEYS) {
    if (draft[key] !== saved[key]) changes[key] = draft[key];
  }
  return changes as Partial<ChangelogSettingsInput>;
}

// ─── Automation presets ─────────────────────────────────────────────
// One question — "how automatic should this be?" — answered with one click.
// A preset pins the toggles that decide whether a human is in the loop; the
// dials under them (delays, thresholds, quiet hours) stay whatever they are,
// so picking a preset never silently rewrites a number somebody tuned.
//
// Presets govern the internal channels only. What users see in-app is a
// separate control on purpose: cranking automation up must never be the thing
// that starts interrupting every user of the product.

export type ChangelogAutomationPreset =
  | "REVIEW_EVERYTHING"
  | "AUTO_WITH_GUARDRAILS"
  | "FULLY_AUTOMATIC";

export const CHANGELOG_AUTOMATION_PRESETS: ChangelogAutomationPreset[] = [
  "REVIEW_EVERYTHING",
  "AUTO_WITH_GUARDRAILS",
  "FULLY_AUTOMATIC",
];

/**
 * Which preset the draft currently amounts to, or null for a hand-tuned mix
 * the page shows as "Custom".
 *
 * Deliberately reads only the gate toggles and, for guardrails, whether a
 * grace period exists at all — so tuning a delay from 60 to 90 minutes keeps
 * the preset lit, while removing the delay entirely is honestly Custom.
 */
export function presetFromSettings(
  draft: Pick<
    ChangelogSettingsDraft,
    | "requireDeployApproval"
    | "requireRoundupApproval"
    | "autoComposeRoundups"
    | "autoPublishDelayMinutes"
  >,
): ChangelogAutomationPreset | null {
  const { requireDeployApproval, requireRoundupApproval, autoComposeRoundups } = draft;

  if (requireDeployApproval && requireRoundupApproval && !autoComposeRoundups) {
    return "REVIEW_EVERYTHING";
  }
  if (!requireDeployApproval && !requireRoundupApproval && autoComposeRoundups) {
    return "FULLY_AUTOMATIC";
  }
  if (
    !requireDeployApproval &&
    requireRoundupApproval &&
    autoComposeRoundups &&
    draft.autoPublishDelayMinutes > 0
  ) {
    return "AUTO_WITH_GUARDRAILS";
  }
  return null;
}

/** The grace period guardrails fall back to when none is set. */
const GUARDRAIL_DELAY_MINUTES = 60;

/**
 * The draft with a preset applied. Only the fields the preset is about are
 * written; everything else — thresholds, quiet windows, channels — survives.
 */
export function applyPreset(
  draft: ChangelogSettingsDraft,
  preset: ChangelogAutomationPreset,
): ChangelogSettingsDraft {
  switch (preset) {
    case "REVIEW_EVERYTHING":
      return {
        ...draft,
        requireDeployApproval: true,
        requireRoundupApproval: true,
        autoComposeRoundups: false,
      };
    case "AUTO_WITH_GUARDRAILS":
      return {
        ...draft,
        requireDeployApproval: false,
        requireRoundupApproval: true,
        autoComposeRoundups: true,
        // A guardrail preset with no grace period is not one, so an absent
        // delay gets the default; a tuned one is left alone.
        autoPublishDelayMinutes:
          draft.autoPublishDelayMinutes > 0
            ? draft.autoPublishDelayMinutes
            : GUARDRAIL_DELAY_MINUTES,
      };
    case "FULLY_AUTOMATIC":
      return {
        ...draft,
        requireDeployApproval: false,
        requireRoundupApproval: false,
        autoComposeRoundups: true,
      };
  }
}

const MINUTES_IN_HOUR = 60;

/** `<input type="time">` speaks `HH:mm`; the record stores minutes past midnight. */
export function minuteToTimeValue(minute: number): string {
  const hours = Math.floor(minute / MINUTES_IN_HOUR);
  const minutes = minute % MINUTES_IN_HOUR;
  return `${`${hours}`.padStart(2, "0")}:${`${minutes}`.padStart(2, "0")}`;
}

/**
 * Returns null for anything that is not a time, which is what a cleared time
 * input reports — the caller keeps the stored value rather than writing a NaN.
 */
export function timeValueToMinute(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * MINUTES_IN_HOUR + minutes;
}

export { listTimezones } from "@/lib/timezones";
